import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { EquityIssuePlans } from "~/app/utils/constants"
import { EquityIssueShape } from "../../hooks/queries/useEquityIssuesHeatmap"
import {
  EquityTypeEnum,
  EquitySubTypeEnum,
  Maybe,
  EquityCompanyPlan,
  EquityGrant,
} from "@oysterhr/schema"

interface vestingHelpingMehtodsProps {
  equityGrant: EquityGrant | undefined
  equityCompanyPlan: Maybe<EquityCompanyPlan> | undefined
}

export const getVestingCliffMonths = ({
  equityGrant,
  equityCompanyPlan,
}: vestingHelpingMehtodsProps) => {
  if (equityGrant) return equityGrant.vestingCliffMonths

  return equityCompanyPlan?.vestingCliffMonths ? equityCompanyPlan.vestingCliffMonths : undefined
}

export const getVestingCliffShares = ({
  equityGrant,
  equityCompanyPlan,
}: vestingHelpingMehtodsProps) => {
  if (equityGrant) return equityGrant.vestingCliffShares
  return equityCompanyPlan?.vestingCliffShares ? equityCompanyPlan.vestingCliffShares : undefined
}

export const useEquityTypeOptions = () => {
  const { t } = useTranslation("equity/EquityGrantPage")
  return useMemo(
    () =>
      Object.values(EquityTypeEnum)
        .map(type => {
          return { label: t(type, { ns: "equity/EquityType" }), value: type }
        })
        .reverse(),
    [t],
  )
}

export const getStockOptionsTypeOptions = () => {
  return Object.values(EquitySubTypeEnum)
    .map(type => {
      return { label: type, value: type }
    })
    .reverse()
}

export const equityHeatMap = (countryInfo: EquityIssueShape, equityType: string) => {
  switch (equityType.toLowerCase()) {
    case EquityIssuePlans.RSU:
      return countryInfo?.rsuRiskTier
    case EquityIssuePlans.SOP:
      return countryInfo?.sopRiskTier
    case EquityIssuePlans.CSH:
      return countryInfo?.cshRiskTier
    default:
      return 0
  }
}

export const useVestingTranslations = (
  vestingScheduleMonths: number | undefined | null,
  vestingCliffMonths: number | undefined | null,
) => {
  const refinedVestingScheduleMonths = vestingScheduleMonths ?? 0
  const refinedVestingCliffMonths = vestingCliffMonths ?? 0

  const { t } = useTranslation("equity/EquityGrantPage")

  const getYearsAndMonths = useCallback(
    (years: number, months: number) => {
      if (years && months)
        return (
          t("years", { count: years, ns: "equity/EquityGrantPage" }) +
          t("and") +
          t("months", { count: months, ns: "equity/EquityGrantPage" })
        )
      if (years) return t("years", { count: years, ns: "equity/EquityGrantPage" })
      if (months) return t("months", { count: months, ns: "equity/EquityGrantPage" })
      return t("months", { count: 0, ns: "equity/EquityGrantPage" })
    },
    [t],
  )

  return useMemo(() => {
    const vestingScheduleYears = Math.floor(refinedVestingScheduleMonths / 12)
    const vestingScheduleMonth = refinedVestingScheduleMonths % 12
    const vestingCliffYears = Math.floor(refinedVestingCliffMonths / 12)
    const vestingCliffMonth = refinedVestingCliffMonths % 12
    const vestingTime = getYearsAndMonths(vestingScheduleYears, vestingScheduleMonth)
    const cliffTime = getYearsAndMonths(vestingCliffYears, vestingCliffMonth)
    // this field will be used in the summary page, where we show remaining time to be
    // vested after the cliff
    const vestingCliffDifference = refinedVestingScheduleMonths - refinedVestingCliffMonths
    const vestingCliffDifferenceYears = Math.floor(vestingCliffDifference / 12)
    const vestingCliffDifferenceMonth = vestingCliffDifference % 12
    const vestingCliffDifferenceTime = getYearsAndMonths(
      vestingCliffDifferenceYears,
      vestingCliffDifferenceMonth,
    )
    return { vestingTime, cliffTime, vestingCliffDifferenceTime }
  }, [refinedVestingScheduleMonths, refinedVestingCliffMonths, getYearsAndMonths])
}
